import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";


const API_BASE =
  process.env.REACT_APP_API_URL ;

  const PAGE_SIZE = 10;

const MONTHS = {
  JANUARY: "01",
  FEBRUARY: "02",
  MARCH: "03",
  APRIL: "04",
  MAY: "05",
  JUNE: "06",
  JULY: "07",
  AUGUST: "08",
  SEPTEMBER: "09",
  OCTOBER: "10",
  NOVEMBER: "11",
  DECEMBER: "12",
};

const COHORT_FIELDS = [
  { name: "BatchStartDate", label: "Batch Start Date", type: "date" },
  { name: "BatchEndDate", label: "Batch End Date", type: "date" },
  { name: "Duration", label: "Duration", type: "text", placeholder: "1.5 Months" },
  { name: "BatchType", label: "Batch Type", type: "text", placeholder: "MORNING / EVENING" },
  { name: "Timings", label: "Timings", type: "text", placeholder: "07:00 AM TO 08:00 AM" },
  { name: "SeatsAvailable", label: "Seats Available", type: "text", placeholder: "25 Seats" },
  { name: "Mode", label: "Mode", type: "text", placeholder: "LIVE/ONLINE" },
  { name: "Language", label: "Language", type: "text", placeholder: "ENGLISH" },
  { name: "Sessions", label: "Sessions", type: "text", placeholder: "MONDAY to FRIDAY" },
  {
    name: "LearningPlatform",
    label: "Learning Platform",
    type: "textarea",
    placeholder: "Practical Infosec LMS + Live Classes",
  },
];

const FEE_FIELDS = [
  { name: "TotalCourseFee", label: "Total Course Fee", type: "number", placeholder: "25000" },
  { name: "AfterOfferCourseFee", label: "After Offer Course Fee", type: "number", placeholder: "19000" },
  { name: "DiscountPercentage", label: "Discount Percentage", type: "number", placeholder: "24" },
  { name: "FirstInstallmentAmount", label: "First Installment Amount", type: "number", placeholder: "12500" },
  { name: "SecondInstallmentAmount", label: "Second Installment Amount", type: "number", placeholder: "6500" },
  { name: "FirstInstallmentLink", label: "First Installment Link", type: "text", placeholder: "https://rzp.io/..." },
  { name: "SecondInstallmentLink", label: "Second Installment Link", type: "text", placeholder: "https://rzp.io/..." },
];

const SECTIONS = {
  juniorCohort: {
    key: "juniorCohort",
    type: "cohort",
    title: "Junior Cohort",
    subtitle: "Manage Junior Penetration Tester cohort details.",
    createLabel: "Create Junior Cohort",
    icon: "🛡️",
    accent: "blue",
    fields: COHORT_FIELDS,
    listUrl: "/junior/cohort-details-junior-without-limit",
    createUrl: "/junior/cohort-details-junior",
    updateUrl: (id) => `/junior/cohort-details-junior/${id}`,
    deleteUrl: "/junior/cohort-details-junior",
    clickField: "BatchStartDate",
  },
  seniorCohort: {
    key: "seniorCohort",
    type: "cohort",
    title: "Senior Cohort",
    subtitle: "Manage Senior Penetration Tester cohort details.",
    createLabel: "Create Senior Cohort",
    icon: "⚔️",
    accent: "purple",
    fields: COHORT_FIELDS,
    listUrl: "/senior/cohort-details-senior-without-limit",
    createUrl: "/senior/cohort-details-senior",
    updateUrl: (id) => `/senior/cohort-details-senior/${id}`,
    deleteUrl: "/senior/cohort-details-senior",
    clickField: "BatchStartDate",
  },
  juniorFee: {
    key: "juniorFee",
    type: "fee",
    title: "Junior Course Fee",
    subtitle: "Manage Junior Penetration Tester course fee and installment links.",
    createLabel: "Create Junior Course Fee",
    icon: "💳",
    accent: "cyan",
    fields: FEE_FIELDS,
    listUrl: "/coursefeejunior/course-fee-junior-without-limit",
    createUrl: "/coursefeejunior/course-fee-junior",
    updateUrl: (id) => `/coursefeejunior/course-fee-junior/${id}`,
    deleteUrl: "/coursefeejunior/course-fee-junior",
    clickField: "TotalCourseFee",
  },
  seniorFee: {
    key: "seniorFee",
    type: "fee",
    title: "Senior Course Fee",
    subtitle: "Manage Senior Penetration Tester course fee and installment links.",
    createLabel: "Create Senior Course Fee",
    icon: "💰",
    accent: "rose",
    fields: FEE_FIELDS,
    listUrl: "/coursefeesenior/course-fee-senior-without-limit",
    createUrl: "/coursefeesenior/course-fee-senior",
    updateUrl: (id) => `/coursefeesenior/course-fee-senior/${id}`,
    deleteUrl: "/coursefeesenior/course-fee-senior",
    clickField: "TotalCourseFee",
  },
};

function getAuthToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("adminToken") ||
    ""
  );
}

function getAdminUser() {
  try {
    const savedUser = localStorage.getItem("adminUser");
    return savedUser ? JSON.parse(savedUser) : null;
  } catch {
    return null;
  }
}

function getAuthHeaders() {
  const token = getAuthToken();

  return token
    ? {
        Authorization: `Bearer ${token}`,
      }
    : {};
}

function getApiError(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong"
  );
}

function toDateInput(value) {
  if (!value) return "";

  const raw = String(value).trim();

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const displayMatch = raw
    .replace(/\s+/g, " ")
    .toUpperCase()
    .match(/^(\d{1,2})\s*-\s*([A-Z]+)\s*-\s*(\d{4})$/);

  if (displayMatch) {
    const day = String(displayMatch[1]).padStart(2, "0");
    const month = MONTHS[displayMatch[2]];
    const year = displayMatch[3];

    if (month) {
      return `${year}-${month}-${day}`;
    }
  }

  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, "0");
    const day = String(parsed.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  }

  return "";
}

function formatDateDisplay(value) {
  if (!value) return "-";

  const input = toDateInput(value);
  if (!input) return String(value);

  const [year, month, day] = input.split("-");
  const monthName =
    Object.entries(MONTHS).find(([, number]) => number === month)?.[0] || month;

  return `${day}-${monthName}-${year}`;
}

function formatCurrency(value) {
  const numberValue = Number(value || 0);

  if (!Number.isFinite(numberValue)) return value || "-";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(numberValue);
}

function buildEmptyForm(section) {
  return section.fields.reduce((acc, field) => {
    acc[field.name] = "";
    return acc;
  }, {});
}

function buildEditForm(section, record) {
  return section.fields.reduce((acc, field) => {
    const value = record?.[field.name] ?? "";

    acc[field.name] =
      field.type === "date" ? toDateInput(value) : String(value ?? "");

    return acc;
  }, {});
}

function AdminWorkForPI() {

  const navigate = useNavigate();





   useEffect(() => {
      
      const token = localStorage.getItem("token");
      const savedRole = localStorage.getItem("role");
      const adminUser = getAdminUser();
  
      const role = adminUser?.Role || savedRole;
  
      if (token && role === "ADMIN") {
        navigate("/admin/admin-work-pi", { replace: true });
        return;
      }
  
      if (token && role && role !== "ADMIN") {
        navigate("/admin/sign_in", { replace: true });
      }
    }, [navigate]);

    
  const [records, setRecords] = useState({
    juniorCohort: [],
    seniorCohort: [],
    juniorFee: [],
    seniorFee: [],
  });

  const [loading, setLoading] = useState({
    juniorCohort: false,
    seniorCohort: false,
    juniorFee: false,
    seniorFee: false,
  });

  const [errors, setErrors] = useState({
    juniorCohort: "",
    seniorCohort: "",
    juniorFee: "",
    seniorFee: "",
  });

  const [activeSection, setActiveSection] = useState("juniorCohort");

  const [sectionPages, setSectionPages] = useState({
  juniorCohort: 1,
  seniorCohort: 1,
  juniorFee: 1,
  seniorFee: 1,
});

const [hiddenSections, setHiddenSections] = useState({
  juniorCohort: false,
  seniorCohort: false,
  juniorFee: false,
  seniorFee: false,
});

  const [editor, setEditor] = useState({
    open: false,
    mode: "create",
    sectionKey: "",
    record: null,
    form: {},
    errors: {},
    saving: false,
  });

  const [deleteModal, setDeleteModal] = useState({
    open: false,
    sectionKey: "",
    record: null,
    confirmText: "",
    deleting: false,
  });

  const [toast, setToast] = useState({
    show: false,
    type: "success",
    message: "",
  });




  const totals = useMemo(() => {
    const allRecordsCount = Object.values(records).reduce(
      (sum, list) => sum + list.length,
      0
    );

    return {
      allRecordsCount,
      juniorCohort: records.juniorCohort.length,
      seniorCohort: records.seniorCohort.length,
      juniorFee: records.juniorFee.length,
      seniorFee: records.seniorFee.length,
    };
  }, [records]);

  useEffect(() => {
    document.title = "Admin Work | Practical Infosec";
    fetchAllSections();
  }, []);

  useEffect(() => {
  setSectionPages((prev) => {
    const nextPages = { ...prev };

    Object.keys(SECTIONS).forEach((sectionKey) => {
      const totalRecords = records[sectionKey]?.length || 0;
      const totalPages = Math.max(1, Math.ceil(totalRecords / PAGE_SIZE));

      if (nextPages[sectionKey] > totalPages) {
        nextPages[sectionKey] = totalPages;
      }

      if (nextPages[sectionKey] < 1) {
        nextPages[sectionKey] = 1;
      }
    });

    return nextPages;
  });
}, [records]);

  function showToast(type, message) {
    setToast({
      show: true,
      type,
      message,
    });

    setTimeout(() => {
      setToast({
        show: false,
        type: "success",
        message: "",
      });
    }, 3200);
  }

  async function fetchSection(sectionKey) {
    const section = SECTIONS[sectionKey];

    setLoading((prev) => ({
      ...prev,
      [sectionKey]: true,
    }));

    setErrors((prev) => ({
      ...prev,
      [sectionKey]: "",
    }));

    try {
      const response = await axios.get(`${API_BASE}${section.listUrl}`, {
        headers: getAuthHeaders(),
      });

      setRecords((prev) => ({
        ...prev,
        [sectionKey]: Array.isArray(response.data?.data)
          ? response.data.data
          : [],
      }));
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        [sectionKey]: getApiError(error),
      }));
    } finally {
      setLoading((prev) => ({
        ...prev,
        [sectionKey]: false,
      }));
    }
  }

  async function fetchAllSections() {
    await Promise.all(Object.keys(SECTIONS).map((key) => fetchSection(key)));
  }

  function toggleSectionVisibility(sectionKey) {
  setHiddenSections((prev) => ({
    ...prev,
    [sectionKey]: !prev[sectionKey],
  }));
}

function changeSectionPage(sectionKey, nextPage) {
  setSectionPages((prev) => ({
    ...prev,
    [sectionKey]: nextPage,
  }));
}

  function openCreate(sectionKey) {
    const section = SECTIONS[sectionKey];

    setEditor({
      open: true,
      mode: "create",
      sectionKey,
      record: null,
      form: buildEmptyForm(section),
      errors: {},
      saving: false,
    });
  }

  function openEdit(sectionKey, record) {
    const section = SECTIONS[sectionKey];

    setEditor({
      open: true,
      mode: "edit",
      sectionKey,
      record,
      form: buildEditForm(section, record),
      errors: {},
      saving: false,
    });
  }

  function closeEditor() {
    setEditor({
      open: false,
      mode: "create",
      sectionKey: "",
      record: null,
      form: {},
      errors: {},
      saving: false,
    });
  }

  function updateEditorField(fieldName, value) {
    setEditor((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [fieldName]: value,
      },
      errors: {
        ...prev.errors,
        [fieldName]: "",
      },
    }));
  }

  function validateEditorForm(section, form) {
    const nextErrors = {};

    section.fields.forEach((field) => {
      const value = form[field.name];

      if (value === undefined || value === null || String(value).trim() === "") {
        nextErrors[field.name] = `${field.label} is required`;
        return;
      }

      if (field.type === "number") {
        const numberValue = Number(value);

        if (!Number.isInteger(numberValue) || numberValue < 0) {
          nextErrors[field.name] = `${field.label} must be a valid positive integer`;
        }
      }
    });

    if (section.type === "cohort") {
      const start = form.BatchStartDate;
      const end = form.BatchEndDate;

      if (start && end && new Date(end) <= new Date(start)) {
        nextErrors.BatchEndDate =
          "Batch End Date must be greater than Batch Start Date";
      }
    }

    return nextErrors;
  }

  function buildPayload(section, form) {
    return section.fields.reduce((payload, field) => {
      if (field.type === "number") {
        payload[field.name] = Number(form[field.name]);
      } else {
        payload[field.name] = String(form[field.name] || "").trim();
      }

      return payload;
    }, {});
  }

  async function submitEditor(event) {
    event.preventDefault();

    const section = SECTIONS[editor.sectionKey];
    const validationErrors = validateEditorForm(section, editor.form);

    if (Object.keys(validationErrors).length > 0) {
      setEditor((prev) => ({
        ...prev,
        errors: validationErrors,
      }));
      return;
    }

    const payload = buildPayload(section, editor.form);

    setEditor((prev) => ({
      ...prev,
      saving: true,
    }));

    try {
      if (editor.mode === "create") {
        await axios.post(`${API_BASE}${section.createUrl}`, payload, {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        });

        showToast("success", `${section.title} created successfully`);
      } else {
        await axios.put(
          `${API_BASE}${section.updateUrl(editor.record.id)}`,
          payload,
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );

        showToast("success", `${section.title} updated successfully`);
      }

      await fetchSection(editor.sectionKey);
      closeEditor();
    } catch (error) {
      showToast("error", getApiError(error));

      setEditor((prev) => ({
        ...prev,
        saving: false,
      }));
    }
  }

  function openDelete(sectionKey, record) {
    setDeleteModal({
      open: true,
      sectionKey,
      record,
      confirmText: "",
      deleting: false,
    });
  }

  function closeDeleteModal() {
    setDeleteModal({
      open: false,
      sectionKey: "",
      record: null,
      confirmText: "",
      deleting: false,
    });
  }

  async function confirmDelete() {
    if (deleteModal.confirmText !== "DELETE") return;

    const section = SECTIONS[deleteModal.sectionKey];

    setDeleteModal((prev) => ({
      ...prev,
      deleting: true,
    }));

    try {
      await axios.delete(`${API_BASE}${section.deleteUrl}`, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        data: {
          id: deleteModal.record.id,
        },
      });

      showToast("success", `${section.title} record deleted successfully`);
      await fetchSection(deleteModal.sectionKey);
      closeDeleteModal();
    } catch (error) {
      showToast("error", getApiError(error));

      setDeleteModal((prev) => ({
        ...prev,
        deleting: false,
      }));
    }
  }

  return (
    <main className="min-h-screen overflow-hidden bg-slate-950">
      <section className="relative px-4 py-10 overflow-hidden md:px-8 lg:px-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.35),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(14,165,233,0.22),transparent_36%),radial-gradient(circle_at_center,rgba(124,58,237,0.16),transparent_46%)]"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.045)_1px,transparent_1px)] bg-[size:44px_44px] opacity-30"></div>

        <div className="relative z-10 mx-auto max-w-[1600px]">
          <header className="flex flex-col gap-6 mb-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <span className="inline-flex items-center gap-2 px-5 py-2 text-xs font-black tracking-widest uppercase border rounded-full text-cyan-300 bg-white/10 border-white/15">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                Practical Infosec Admin Console
              </span>

              <h1 className="mt-5 text-4xl font-black tracking-tight text-white md:text-6xl">
                Admin Work{" "}
                <span className="text-transparent bg-gradient-to-r from-cyan-300 via-blue-300 to-purple-300 bg-clip-text">
                  For PI
                </span>
              </h1>

              <p className="max-w-4xl mt-5 text-lg font-semibold leading-8 text-slate-300">
                Manage live cohort details and course fee records for Junior and
                Senior Penetration Tester programs.
              </p>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={fetchAllSections}
                className="px-6 py-3 text-sm font-black text-white transition border rounded-2xl border-white/15 bg-white/10 hover:bg-white hover:text-slate-950"
              >
                Refresh All
              </button>

              <button
                type="button"
                onClick={() => openCreate(activeSection)}
                className="px-6 py-3 text-sm font-black text-white transition rounded-2xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-105 shadow-[0_0_35px_rgba(37,99,235,0.45)]"
              >
                + Create Current Section
              </button>
            </div>
          </header>

          <section className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 xl:grid-cols-5">
            <MetricCard title="Total Records" value={totals.allRecordsCount} icon="📊" />
            <MetricCard title="Junior Cohorts" value={totals.juniorCohort} icon="🛡️" />
            <MetricCard title="Senior Cohorts" value={totals.seniorCohort} icon="⚔️" />
            <MetricCard title="Junior Fees" value={totals.juniorFee} icon="💳" />
            <MetricCard title="Senior Fees" value={totals.seniorFee} icon="💰" />
          </section>

          <section className="p-3 mb-8 border rounded-[2rem] bg-white/10 border-white/15 backdrop-blur">
  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
    {Object.values(SECTIONS).map((section) => {
      const isHidden = hiddenSections[section.key];

      return (
        <div
          key={section.key}
          className={`p-4 transition border rounded-3xl ${
            activeSection === section.key
              ? "bg-white text-slate-950 border-white shadow-2xl"
              : "bg-white/5 text-white border-white/10 hover:bg-white/10"
          } ${isHidden ? "opacity-60" : ""}`}
        >
          <button
            type="button"
            onClick={() => setActiveSection(section.key)}
            className="w-full text-left"
          >
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center text-xl text-white w-11 h-11 rounded-2xl bg-slate-950">
                {section.icon}
              </span>

              <div>
                <p className="text-sm font-black">{section.title}</p>

                <p
                  className={`mt-1 text-xs font-semibold ${
                    activeSection === section.key
                      ? "text-slate-500"
                      : "text-slate-300"
                  }`}
                >
                  {records[section.key]?.length || 0} records
                </p>
              </div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => toggleSectionVisibility(section.key)}
            className={`w-full px-3 py-2 mt-4 text-xs font-black transition rounded-xl ${
              isHidden
                ? "bg-green-600 text-white hover:bg-green-700"
                : activeSection === section.key
                ? "bg-slate-950 text-white hover:bg-slate-800"
                : "bg-white/10 text-white hover:bg-white hover:text-slate-950"
            }`}
          >
            {isHidden ? "Unhide Section" : "Hide Section"}
          </button>
        </div>
      );
    })}
  </div>
</section>

          <div className="space-y-10">
            {Object.values(SECTIONS).map((section) => (
             <AdminSection
  key={section.key}
  section={section}
  active={activeSection === section.key}
  records={records[section.key] || []}
  loading={loading[section.key]}
  error={errors[section.key]}
  page={sectionPages[section.key] || 1}
  pageSize={PAGE_SIZE}
  hidden={hiddenSections[section.key]}
  onRefresh={() => fetchSection(section.key)}
  onCreate={() => openCreate(section.key)}
  onEdit={(record) => openEdit(section.key, record)}
  onDelete={(record) => openDelete(section.key, record)}
  onPageChange={(page) => changeSectionPage(section.key, page)}
  onToggleHidden={() => toggleSectionVisibility(section.key)}
/>
            ))}
          </div>
        </div>
      </section>

      {editor.open && (
        <EditorModal
          editor={editor}
          section={SECTIONS[editor.sectionKey]}
          onClose={closeEditor}
          onSubmit={submitEditor}
          onChange={updateEditorField}
        />
      )}

      {deleteModal.open && (
        <DeleteModal
          deleteModal={deleteModal}
          section={SECTIONS[deleteModal.sectionKey]}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          onChange={(value) =>
            setDeleteModal((prev) => ({
              ...prev,
              confirmText: value,
            }))
          }
        />
      )}

      {toast.show && (
        <div
          className={`fixed z-[100] px-5 py-4 text-sm font-black text-white border shadow-2xl rounded-2xl right-5 top-5 ${
            toast.type === "success"
              ? "bg-green-600 border-green-300"
              : "bg-red-600 border-red-300"
          }`}
        >
          {toast.message}
        </div>
      )}
    </main>
  );
}

function MetricCard({ title, value, icon }) {
  return (
    <div className="p-5 border rounded-3xl bg-white/10 border-white/15 backdrop-blur">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs font-black tracking-widest uppercase text-slate-300">
            {title}
          </p>

          <p className="mt-2 text-4xl font-black text-white">{value}</p>
        </div>

        <span className="flex items-center justify-center text-2xl w-14 h-14 rounded-2xl bg-white/10">
          {icon}
        </span>
      </div>
    </div>
  );
}

function AdminSection({
  section,
  active,
  records,
  loading,
  error,
  page,
  pageSize,
  hidden,
  onRefresh,
  onCreate,
  onEdit,
  onDelete,
  onPageChange,
  onToggleHidden,
}) {
  const totalRecords = records.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);

  const startIndex = (safePage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRecords = records.slice(startIndex, endIndex);

  return (
    <section
      className={`overflow-hidden border shadow-2xl rounded-[2rem] transition ${
        active
          ? "border-cyan-300/50 bg-white"
          : "border-white/15 bg-white/95"
      } ${hidden ? "opacity-80" : ""}`}
    >
      <div className="flex flex-col gap-5 p-6 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
            {section.type === "cohort" ? "Cohort Management" : "Fee Management"}
          </p>

          <h2 className="mt-2 text-3xl font-black text-white">
            {section.icon} {section.title}
          </h2>

          <p className="mt-2 text-sm font-semibold leading-6 text-slate-300">
            {section.subtitle}
          </p>

          <div className="flex flex-wrap gap-2 mt-4">
            <span className="px-3 py-1 text-xs font-black text-white border rounded-full bg-white/10 border-white/15">
              Total Records: {totalRecords}
            </span>

            <span className="px-3 py-1 text-xs font-black text-white border rounded-full bg-white/10 border-white/15">
              Showing: {hidden ? 0 : paginatedRecords.length}
            </span>

            <span className="px-3 py-1 text-xs font-black text-white border rounded-full bg-white/10 border-white/15">
              Page: {safePage} / {totalPages}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onToggleHidden}
            className={`px-5 py-3 text-sm font-black text-white transition border rounded-xl ${
              hidden
                ? "bg-green-600 border-green-400 hover:bg-green-700"
                : "bg-white/10 border-white/15 hover:bg-white hover:text-slate-950"
            }`}
          >
            {hidden ? "Unhide Section" : "Hide Section"}
          </button>

          <button
            type="button"
            onClick={onRefresh}
            className="px-5 py-3 text-sm font-black text-white transition border rounded-xl border-white/15 bg-white/10 hover:bg-white hover:text-slate-950"
          >
            Refresh
          </button>

          <button
            type="button"
            onClick={onCreate}
            className="px-5 py-3 text-sm font-black text-white transition rounded-xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-105 shadow-[0_0_35px_rgba(14,165,233,0.45)]"
          >
            + {section.createLabel}
          </button>
        </div>
      </div>

      {hidden ? (
        <div className="p-8">
          <div className="p-8 text-center border rounded-3xl bg-slate-50 border-slate-200">
            <p className="text-5xl">{section.icon}</p>

            <h3 className="mt-4 text-2xl font-black text-slate-950">
              {section.title} Hidden
            </h3>

            <p className="max-w-xl mx-auto mt-2 text-sm font-semibold leading-6 text-slate-500">
              This section is currently hidden from view. Records are not
              deleted. Click Unhide Section to show the table again.
            </p>

            <button
              type="button"
              onClick={onToggleHidden}
              className="px-6 py-3 mt-5 text-sm font-black text-white transition bg-green-600 rounded-xl hover:bg-green-700"
            >
              Unhide Section
            </button>
          </div>
        </div>
      ) : (
        <div className="p-5">
          {loading && (
            <div className="p-6 text-sm font-black text-center border rounded-2xl bg-slate-50 border-slate-200 text-slate-600">
              Loading records...
            </div>
          )}

          {!loading && error && (
            <div className="p-5 text-sm font-black text-red-700 border border-red-100 rounded-2xl bg-red-50">
              {error}
            </div>
          )}

          {!loading && !error && records.length === 0 && (
            <div className="p-10 text-center border rounded-3xl bg-slate-50 border-slate-200">
              <p className="text-4xl">{section.icon}</p>

              <h3 className="mt-3 text-xl font-black text-slate-950">
                No Records Found
              </h3>

              <p className="mt-2 text-sm font-semibold text-slate-500">
                Create the first record for this section.
              </p>
            </div>
          )}

          {!loading && !error && records.length > 0 && (
            <>
              <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
                <p className="text-sm font-black text-slate-700">
                  Showing{" "}
                  <span className="text-blue-700">
                    {startIndex + 1}
                  </span>{" "}
                  to{" "}
                  <span className="text-blue-700">
                    {Math.min(endIndex, totalRecords)}
                  </span>{" "}
                  of{" "}
                  <span className="text-blue-700">{totalRecords}</span>{" "}
                  records
                </p>

                <PaginationControls
                  page={safePage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              </div>

              <div className="overflow-x-auto border rounded-3xl border-slate-200">
                {section.type === "cohort" ? (
                  <CohortTable
                    section={section}
                    records={paginatedRecords}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                ) : (
                  <FeeTable
                    section={section}
                    records={paginatedRecords}
                    onEdit={onEdit}
                    onDelete={onDelete}
                  />
                )}
              </div>

              <div className="flex flex-col gap-3 mt-4 lg:flex-row lg:items-center lg:justify-between">
                <p className="text-xs font-bold text-slate-500">
                  10 records per page
                </p>

                <PaginationControls
                  page={safePage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                />
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}

function PaginationControls({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) {
    return null;
  }

  const pages = [];

  for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
    if (
      pageNumber === 1 ||
      pageNumber === totalPages ||
      Math.abs(pageNumber - page) <= 1
    ) {
      pages.push(pageNumber);
    }
  }

  const uniquePages = [...new Set(pages)];

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        disabled={page === 1}
        onClick={() => onPageChange(page - 1)}
        className="px-4 py-2 text-xs font-black transition border rounded-xl border-slate-200 text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Previous
      </button>

      {uniquePages.map((pageNumber, index) => {
        const previousPage = uniquePages[index - 1];
        const showDots = previousPage && pageNumber - previousPage > 1;

        return (
          <span key={pageNumber} className="flex items-center gap-2">
            {showDots && (
              <span className="px-2 text-xs font-black text-slate-400">
                ...
              </span>
            )}

            <button
              type="button"
              onClick={() => onPageChange(pageNumber)}
              className={`w-10 h-10 text-xs font-black transition rounded-xl ${
                page === pageNumber
                  ? "bg-blue-700 text-white shadow-lg"
                  : "bg-white text-slate-700 border border-slate-200 hover:bg-blue-50 hover:text-blue-700"
              }`}
            >
              {pageNumber}
            </button>
          </span>
        );
      })}

      <button
        type="button"
        disabled={page === totalPages}
        onClick={() => onPageChange(page + 1)}
        className="px-4 py-2 text-xs font-black transition border rounded-xl border-slate-200 text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Next
      </button>
    </div>
  );
}

function CohortTable({ section, records, onEdit, onDelete }) {
  return (
    <table className="min-w-[1200px] w-full text-left">
      <thead className="bg-slate-100">
        <tr>
          {[
            "ID",
            "Batch Start Date",
            "Batch End Date",
            "Duration",
            "Batch Type",
            "Timings",
            "Seats",
            "Mode",
            "Actions",
          ].map((heading) => (
            <th
              key={heading}
              className="px-4 py-4 text-xs font-black tracking-widest uppercase text-slate-600"
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>

      <tbody className="divide-y divide-slate-200">
        {records.map((record) => (
          <tr key={record.id} className="transition hover:bg-blue-50/60">
            <td className="px-4 py-4 text-sm font-black text-slate-700">
              #{record.id}
            </td>

            <td className="px-4 py-4">
              <button
                type="button"
                onClick={() => onEdit(record)}
                className="text-sm font-black text-blue-700 underline underline-offset-4 hover:text-blue-950"
              >
                {formatDateDisplay(record.BatchStartDate)}
              </button>
            </td>

            <td className="px-4 py-4 text-sm font-bold text-slate-700">
              {formatDateDisplay(record.BatchEndDate)}
            </td>

            <td className="px-4 py-4 text-sm font-bold text-slate-700">
              {record.Duration || "-"}
            </td>

            <td className="px-4 py-4">
              <span className="px-3 py-1 text-xs font-black text-white rounded-full bg-slate-950">
                {record.BatchType || "-"}
              </span>
            </td>

            <td className="px-4 py-4 text-sm font-bold text-slate-700">
              {record.Timings || "-"}
            </td>

            <td className="px-4 py-4 text-sm font-bold text-green-700">
              {record.SeatsAvailable || "-"}
            </td>

            <td className="px-4 py-4 text-sm font-bold text-slate-700">
              {record.Mode || "-"}
            </td>

            <td className="px-4 py-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onEdit(record)}
                  className="px-4 py-2 text-xs font-black text-white transition bg-blue-700 rounded-xl hover:bg-blue-800"
                >
                  View / Edit
                </button>

                <button
                  type="button"
                  onClick={() => onDelete(record)}
                  className="px-4 py-2 text-xs font-black text-white transition bg-red-600 rounded-xl hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FeeTable({ section, records, onEdit, onDelete }) {
  return (
    <table className="min-w-[1200px] w-full text-left">
      <thead className="bg-slate-100">
        <tr>
          {[
            "ID",
            "Total Course Fee",
            "Offer Fee",
            "Discount",
            "1st Installment",
            "2nd Installment",
            "Updated At",
            "Actions",
          ].map((heading) => (
            <th
              key={heading}
              className="px-4 py-4 text-xs font-black tracking-widest uppercase text-slate-600"
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>

      <tbody className="divide-y divide-slate-200">
        {records.map((record) => (
          <tr key={record.id} className="transition hover:bg-blue-50/60">
            <td className="px-4 py-4 text-sm font-black text-slate-700">
              #{record.id}
            </td>

            <td className="px-4 py-4">
              <button
                type="button"
                onClick={() => onEdit(record)}
                className="text-sm font-black text-blue-700 underline underline-offset-4 hover:text-blue-950"
              >
                {formatCurrency(record.TotalCourseFee)}
              </button>
            </td>

            <td className="px-4 py-4 text-sm font-black text-green-700">
              {formatCurrency(record.AfterOfferCourseFee)}
            </td>

            <td className="px-4 py-4">
              <span className="px-3 py-1 text-xs font-black text-white rounded-full bg-rose-600">
                {record.DiscountPercentage || 0}% OFF
              </span>
            </td>

            <td className="px-4 py-4 text-sm font-bold text-slate-700">
              {formatCurrency(record.FirstInstallmentAmount)}
            </td>

            <td className="px-4 py-4 text-sm font-bold text-slate-700">
              {formatCurrency(record.SecondInstallmentAmount)}
            </td>

            <td className="px-4 py-4 text-xs font-semibold text-slate-500">
              {record.updated_at
                ? new Date(record.updated_at).toLocaleString()
                : "-"}
            </td>

            <td className="px-4 py-4">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onEdit(record)}
                  className="px-4 py-2 text-xs font-black text-white transition bg-blue-700 rounded-xl hover:bg-blue-800"
                >
                  View / Edit
                </button>

                <button
                  type="button"
                  onClick={() => onDelete(record)}
                  className="px-4 py-2 text-xs font-black text-white transition bg-red-600 rounded-xl hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EditorModal({ editor, section, onClose, onSubmit, onChange }) {
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8 bg-slate-950/80 backdrop-blur">
      <div className="w-full max-w-5xl overflow-hidden bg-white shadow-2xl rounded-[2rem]">
        <div className="flex flex-col gap-4 px-6 py-5 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-950 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-black tracking-widest uppercase text-cyan-300">
              {editor.mode === "create" ? "Create Record" : "View / Edit Record"}
            </p>

            <h2 className="mt-1 text-2xl font-black text-white">
              {section.icon}{" "}
              {editor.mode === "create"
                ? section.createLabel
                : `${section.title} #${editor.record?.id}`}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center text-2xl font-black text-white transition rounded-full w-11 h-11 bg-white/10 hover:bg-white hover:text-slate-950"
          >
            ×
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {section.fields.map((field) => (
              <FormField
                key={field.name}
                field={field}
                value={editor.form[field.name] || ""}
                error={editor.errors[field.name]}
                onChange={(value) => onChange(field.name, value)}
              />
            ))}
          </div>

          <div className="flex flex-col gap-3 pt-6 mt-6 border-t border-slate-200 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-black transition border rounded-xl text-slate-700 border-slate-200 hover:bg-slate-100"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={editor.saving}
              className="px-6 py-3 text-sm font-black text-white transition rounded-xl bg-gradient-to-r from-blue-700 to-cyan-500 hover:scale-105 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {editor.saving
                ? "Saving..."
                : editor.mode === "create"
                ? "Create"
                : "Update All Details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function FormField({ field, value, error, onChange }) {
  const baseClass = `w-full px-4 py-3 text-sm font-semibold transition bg-white border shadow-sm outline-none rounded-xl focus:ring-4 focus:ring-blue-100 ${
    error
      ? "border-red-500"
      : value
      ? "border-green-500"
      : "border-slate-200 focus:border-blue-500"
  }`;

  return (
    <div className={field.type === "textarea" ? "md:col-span-2" : ""}>
      <label className="block mb-2 text-sm font-black text-slate-800">
        {field.label}
      </label>

      {field.type === "textarea" ? (
        <textarea
          value={value}
          rows={4}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
          className={`${baseClass} resize-none`}
        />
      ) : (
        <input
          type={field.type}
          value={value}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
          className={baseClass}
        />
      )}

      {error && (
        <p className="mt-1 text-xs font-semibold text-red-600">{error}</p>
      )}
    </div>
  );
}

function DeleteModal({ deleteModal, section, onClose, onConfirm, onChange }) {
  const disabled = deleteModal.confirmText !== "DELETE" || deleteModal.deleting;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-4 bg-slate-950/80 backdrop-blur">
      <div className="w-full max-w-xl overflow-hidden bg-white shadow-2xl rounded-[2rem]">
        <div className="p-6 bg-gradient-to-r from-red-700 to-rose-700">
          <p className="text-xs font-black tracking-widest text-red-100 uppercase">
            Dangerous Action
          </p>

          <h2 className="mt-2 text-3xl font-black text-white">
            Delete {section.title} Record?
          </h2>

          <p className="mt-3 text-sm font-semibold leading-6 text-red-100">
            This action cannot be undone. To confirm deletion, type{" "}
            <span className="font-black text-white">DELETE</span>.
          </p>
        </div>

        <div className="p-6">
          <div className="p-4 border rounded-2xl bg-slate-50 border-slate-200">
            <p className="text-sm font-black text-slate-950">
              Record ID: #{deleteModal.record?.id}
            </p>

            <p className="mt-1 text-sm font-semibold text-slate-600">
              {section.type === "cohort"
                ? `Batch Start Date: ${formatDateDisplay(
                    deleteModal.record?.BatchStartDate
                  )}`
                : `Total Course Fee: ${formatCurrency(
                    deleteModal.record?.TotalCourseFee
                  )}`}
            </p>
          </div>

          <label className="block mt-5 mb-2 text-sm font-black text-slate-800">
            Type DELETE to confirm
          </label>

          <input
            type="text"
            value={deleteModal.confirmText}
            onChange={(event) => onChange(event.target.value)}
            placeholder="DELETE"
            className="w-full px-4 py-3 text-sm font-black tracking-widest uppercase border outline-none rounded-xl border-slate-200 focus:border-red-500 focus:ring-4 focus:ring-red-100"
          />

          <div className="flex flex-col gap-3 mt-6 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-black transition border rounded-xl text-slate-700 border-slate-200 hover:bg-slate-100"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={onConfirm}
              disabled={disabled}
              className="px-6 py-3 text-sm font-black text-white transition bg-red-600 rounded-xl hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {deleteModal.deleting ? "Deleting..." : "Delete Record"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminWorkForPI;